"use client"

import { useCallback, useEffect, useState, type ReactNode } from "react"
import { ExternalLink, Link2 } from "lucide-react"
import { useTranslations } from "next-intl"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { PatreonLogo } from "@/components/patreon/patreon-logo"
import { useAuthStore } from "@/lib/auth-store"
import { usePatreonMode } from "@/lib/use-patreon-mode"

// "Support on Patreon" (docs/PATREON_PLAN_C.md, step 4): while Patreon mode is ON, this replaces the Upgrade / Subscribe buttons.
// One flow: a signed-in user who is not linked yet goes through /api/patreon/connect, which links the accounts and (when they
// have no perks yet) sends them straight on to the Patreon page to pick a tier. Their perks switch on from the pledge webhook, so they never
// have to come back and press "link". The explainer modal always opens first (owner: it is how people understand the flow);
// its main button is what takes them to Patreon.

export const PATREON_GRADIENT = "linear-gradient(135deg, #f96854 0%, #ff8a3d 45%, #7c3aed 100%)"
type LinkState = { configured: boolean; linked: boolean } | null

/** Whether the signed-in user's Patreon is already linked (null while loading or signed out). */
function usePatreonLinkState(): LinkState {
  const user = useAuthStore((s) => s.user)
  const [state, setState] = useState<LinkState>(null)
  useEffect(() => {
    if (!user) return setState(null)
    let alive = true
    fetch("/api/patreon/status", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => alive && setState(j ? { configured: j.configured === true, linked: j.linked === true } : null))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [user])
  return state
}

/** What the main Patreon button does right now. "soon" = no Patreon page link set yet. */
function usePatreonAction() {
  const { pageUrl, loading } = usePatreonMode()
  const link = usePatreonLinkState()
  const kind: "loading" | "soon" | "link_and_pledge" | "open_page" = loading
    ? "loading"
    : !pageUrl
      ? "soon"
      : link?.configured && !link.linked
        ? "link_and_pledge"
        : "open_page"
  const run = useCallback(() => {
    if (kind === "link_and_pledge") window.location.href = "/api/patreon/connect"
    else if (kind === "open_page" && pageUrl) window.open(pageUrl, "_blank", "noopener,noreferrer")
  }, [kind, pageUrl])
  return { kind, run, canJustLink: !!link?.configured && !link.linked }
}

/** Opens the Patreon explainer modal. Render `modal` somewhere in the tree and call `start()` from a click. */
export function usePatreonSupportFlow() {
  const [open, setOpen] = useState(false)
  const start = useCallback(() => setOpen(true), [])
  return { start, modal: <PatreonSupportModal open={open} onOpenChange={setOpen} /> }
}

function Steps() {
  const t = useTranslations("patreonSupport")
  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold">{t("stepsTitle")}</p>
      <ol className="space-y-2 text-sm text-muted-foreground">
        {(["step1", "step2", "step3"] as const).map((k, i) => (
          <li key={k} className="flex items-start gap-3">
            <span
              className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
              style={{ background: PATREON_GRADIENT }}
            >
              {i + 1}
            </span>
            <span>{t(k)}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}

/** The gradient call-to-action: links + opens Patreon (or says it is coming soon when no Patreon page link is set). */
export function PatreonContinueButton({ className, onDone }: { className?: string; onDone?: () => void }) {
  const t = useTranslations("patreonSupport")
  const { kind, run } = usePatreonAction()
  if (kind === "loading" || kind === "soon") {
    return (
      <Button disabled className={className}>
        {kind === "loading" ? t("continue") : t("comingSoon")}
      </Button>
    )
  }
  return (
    <Button
      className={`gap-2 border-0 font-bold text-white hover:opacity-95 ${className ?? ""}`}
      style={{ background: PATREON_GRADIENT }}
      onClick={() => {
        run()
        onDone?.()
      }}
    >
      <PatreonLogo className="h-4 w-4" />
      {t("continue")}
      {kind === "open_page" && <ExternalLink className="h-3.5 w-3.5 opacity-80" aria-hidden />}
    </Button>
  )
}

export function PatreonSupportModal({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const t = useTranslations("patreonSupport")
  const { canJustLink } = usePatreonAction()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PatreonLogo className="h-5 w-5 text-[#f96854]" />
            {t("title")}
          </DialogTitle>
          <DialogDescription>{t("intro")}</DialogDescription>
        </DialogHeader>
        <Steps />
        <p className="rounded-lg border border-dashed bg-muted/40 px-3 py-2 text-xs text-muted-foreground">{t("disclaimer")}</p>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <PatreonContinueButton className="w-full" onDone={() => onOpenChange(false)} />
          {/* Already pledged before linking: just link, no trip to the tier page. */}
          {canJustLink && (
            <a
              href="/api/patreon/connect"
              className="flex items-center justify-center gap-1.5 py-1 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              <Link2 className="h-3.5 w-3.5" aria-hidden />
              {t("justLink")}
            </a>
          )}
          <Button variant="ghost" className="w-full" onClick={() => onOpenChange(false)}>
            {t("close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Orange-to-purple "Support on Patreon" button: opens the explainer modal. Pass `children` to replace the label.
 * `href`: go to that in-app page instead (the profile dropdown opens /subscribe/patreon).
 */
export function SupportOnPatreonButton({ className, children, href }: { className?: string; children?: ReactNode; href?: string }) {
  const t = useTranslations("patreonSupport")
  const router = useRouter()
  const { start, modal } = usePatreonSupportFlow()
  const onClick = () => (href ? router.push(href) : start())
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        style={{ background: PATREON_GRADIENT, boxShadow: "0 4px 16px rgba(249,104,84,0.35)" }}
        className={`flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-bold text-white transition-all hover:scale-[1.01] hover:opacity-95 active:scale-[0.99] ${className ?? "w-full"}`}
      >
        <PatreonLogo className="h-4 w-4 shrink-0" />
        <span className="drop-shadow-sm">{children ?? t("button")}</span>
      </button>
      {modal}
    </>
  )
}

/** Full-page version, shown at /subscribe while Patreon mode is ON so every old "Upgrade" link lands somewhere that makes sense. */
export function PatreonSupportPage() {
  const t = useTranslations("patreonSupport")
  return (
    <div className="mx-auto flex min-h-[60vh] w-full max-w-md flex-col justify-center gap-5 px-4 py-12">
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-black tracking-tight">{t("pageTitle")}</h1>
        <p className="text-muted-foreground">{t("pageIntro")}</p>
        <p className="text-sm text-muted-foreground">{t("intro")}</p>
      </div>
      <Steps />
      <p className="rounded-lg border border-dashed bg-muted/40 px-3 py-2 text-xs text-muted-foreground">{t("disclaimer")}</p>
      <PatreonContinueButton className="w-full" />
    </div>
  )
}
